<template>
  <component
    :is="htmlTag"
    v-if="initialized && matchesContainsRule"
    ref="root"
    class="property"
    :class="[
      hasValue || (original?.defaultOptions?.length && showChanges) || current?.fallback !== undefined ? 'has-value' : 'no-value',
      {
        ...(editMode && {
          typing: !!innerSearchTerm,
          modified: containsChanges && !showChanges,
        }),
        static: isStatic,
        'd-flex flex-wrap': tag !== 'li',

        preliminary: current?.status === 'preliminary',
        placeholder: current?.placeholder && !hasValue,
        empty: !current?.placeholder && !hasValue,

        resettable: alwaysResettable && propertyIsResettable,
        discreet: discreetInputs,
      },
    ]"
    :title="hoverTitle"
    @click="handleResetStaticValue()"
  >
    <i v-if="alwaysResettable && propertyIsResettable" class="resettable-icon fa fa-undo" />

    <!----------->
    <!-- Label -->
    <!----------->
    <slot name="before" :value="current?.displayValue" :is-sku="isSku" />

    <PropertyLabelComponent
      v-if="labelShouldBeShown"
      class="label"
      :property="property"
      :disabled="!editMode"
      :no-value="!hasValue && (!showChanges || !original?.defaultOptions?.length)"
      :title="
        alwaysResettable
          ? appTranslate('Click to reset %s', [label])
          : editMode
            ? ''
            : [property.info?.[store.infoLanguage], belongsTo?.join(', ')].join('\n\n')
      "
    />

    <!-------------->
    <!-- Editable -->
    <!-------------->
    <div v-if="!isStatic" class="d-flex w-100 position-relative">
      <div class="position-relative w-100 d-flex" :class="[property.valueType]">
        <CategorySelectComponent
          v-if="property.type === 'categories' && Array.isArray(propertyValue)"
          :key="categoryOptionsUpdateKey"
          v-model="propertyValue"
          class="state"
          :original="product?.categories"
          :discreet-inputs="discreetInputs"
          :placeholder="statePlaceholder"
          :has-changed-status="hasChangedStatus"
          :options="options"
          :max-length="property.maxLength"
          allow-custom-values
          @new="propertyStore.addStagedOption(props.property, { value: $event.value, parentId: $event.parentId }), categoryOptionsUpdateKey++"
          @hide-staged="propertyStore.hideStagedOption(props.property, $event.value)"
          @keydown.escape.stop
        />

        <ImprovedCustomDropdown
          v-else-if="shouldUseCustomDropdown && Array.isArray(propertyValue)"
          :id="property.defaultName"
          v-model="propertyValue"
          class="state"
          :class="{
            'bg-white': !containsChanges || (property.allowMultipleValues && propertyValue.length && !hasChangedStatus && !hasRemovedValue),
          }"
          :allow-custom-values="props.property.type !== 'basic' && store.userPrivileges.has('createTaxonomicObjects') ? 'confirm' : false"
          :multiple="property.allowMultipleValues"
          :options="options"
          :original-options="new Set(original?.defaultOptions || [])"
          :discreet-inputs="discreetInputs"
          :deletable="!property.undeletable"
          :input-type="propShouldBeNumeric ? 'number' : 'text'"
          :placeholder="!propertyValue?.length ? statePlaceholder : undefined"
          :max-length="property.maxLength"
          @search="innerSearchTerm = $event?.value"
          @new="propertyStore.addStagedOption(props.property, $event.value)"
          @hide-staged="propertyStore.hideStagedOption(props.property, $event.value)"
          @keydown="handleFocusedInput"
        />

        <EditableComponent
          v-else-if="property.multiLine && typeof propertyValue === 'string'"
          v-model.lazy.trim="propertyValue"
          tag="div"
          class="form-control multi-line state"
          :class="[
            {
              [property.defaultName]: property.defaultName?.includes('description'),
              'p-0': discreetInputs,
              // 'preliminary-text': current?.status === 'preliminary',
            },
          ]"
          :placeholder="statePlaceholder"
          :max-length="property.maxLength"
          :discreet-inputs="discreetInputs"
          :placeholder-class="{ 'preliminary-text': current?.status === 'preliminary' }"
          @input="handleInput"
          @mousedown.stop
          @keydown="handleFocusedInput"
        />

        <input
          v-else-if="restrictedToNumericValue"
          v-model.lazy="propertyValue"
          type="number"
          min="0"
          class="form-control state"
          :class="{
            'p-0': discreetInputs,
          }"
          :step="property.step"
          :placeholder="statePlaceholder || numericPlaceholder"
          @focus="numericPlaceholder = `${appTranslate('Enter a numeric value')} (${translate(property.valueType, 'application', 'lowercase')})`"
          @blur="numericPlaceholder = ''"
          @mousedown.stop
          @input="handleInput"
          @keydown="handleFocusedInput"
          @beforeinput="validateNumericInput($event, property.valueType === 'float')"
        />

        <input
          v-else
          v-model.lazy="propertyValue"
          :type="property.valueType === 'date' && current?.status !== 'checked' ? 'date' : 'text'"
          class="form-control state"
          :class="{
            'p-0': discreetInputs,
          }"
          :max="property.valueType === 'date' ? '9999-12-31' : undefined"
          :maxlength="property.maxLength"
          :placeholder="statePlaceholder"
          @mousedown.stop
          @input="handleInput"
          @keydown="handleFocusedInput"
        />
      </div>

      <!-- Undo -->
      <button
        v-if="undoIcon && containsChanges && propertyIsResettable && productPropertyIsEditable(property, product, productInput)"
        class="action undo"
        @click="resetProperty()"
        @mousedown.stop
      >
        <i class="fa fa-undo" :class="undoIcon" />
      </button>
    </div>

    <!------------------->
    <!-- Static values -->
    <!------------------->
    <template v-else>
      <div v-if="activateFilterLinks && current?.filters?.length" class="state">
        <!-- Filter links -->
        <template v-for="(filter, i) of current?.filters" :key="i">
          <router-link class="filter-link" :to="filter.link" @mousedown.stop>
            <slot v-if="slots.static" name="static" :value="current?.displayValue" :is-sku="isSku" />
            <template v-else>
              {{ filter.displayValue }}
            </template>
          </router-link>
          <span v-if="i < current.filters.length - 1" v-html="valueSeparator" />
        </template>
      </div>

      <!-- Custom display (slot) -->
      <span
        v-else-if="slots.static && current?.status !== 'checked'"
        class="state"
        :class="{ 'preliminary-inner': current?.status === 'preliminary', sku: isSku }"
      >
        <slot name="static" :value="current?.displayValue" :is-sku="isSku" :current="current" />
      </span>

      <!-- <div v-else-if="isAvailableCountriesProperty(property) && !discreetInputs" class="available-countries">
        <div v-for="(country, i) of current?.defaultOptions" :key="country">
          {{ current?.options?.[i] }}
          <img :src="getCountryFlag(country as string)" class="tiny-flag" />
        </div>
      </div> -->

      <!-- Default display -->
      <TinyPropertyStateChangeComponent
        v-else-if="showChanges"
        :current="current"
        :original="original"
        :show-value-as-html="showValueAsHtml"
        :contains-changes="containsChanges"
      />
      <TinyPropertyStateComponent v-else :property-return="current" :show-value-as-html="showValueAsHtml" />
    </template>

    <slot name="after" :value="current?.displayValue" :is-sku="isSku" />
  </component>
</template>

<script lang="ts" setup>
import ImprovedCustomDropdown from '@/components/ImprovedCustomDropdown.vue'
import CategorySelectComponent from '@/components/products/CategorySelectComponent.vue'
import EditableComponent from '@/components/simple/EditableComponent.vue'
import { useTranslation } from '@/composables/useTranslation'
import { Culture, Language } from '@/models/Global'
import { ProductInput } from '@/models/Import'
import Message from '@/models/Message'
import { SimplifiedProduct } from '@/models/Product'
import {
  EditableAdvancedProperty,
  EmptyCheckedPlaceholder,
  EmptyPreliminaryPlaceholder,
  PropertyOptions,
  PropertyReturn,
  PropertySetterValueType,
  PropertyVisibility,
  SimplePropertySetterValueType,
} from '@/models/Property'
import PropertyService from '@/services/PropertyService'
import { useDefaultStore } from '@/store'
import { useProductsTableUtilityStore } from '@/store/productsTableUtilityStore'
import { usePropertyStore } from '@/store/propertyStore'
import {
  isAvailableStatusProperty,
  isSkuProperty,
  productPropertyIsEditable,
  propertyCanGenerateEan,
  propertyHasStaticValues,
  valueTypeIsNumeric,
} from '@/utils/propertyUtils'
import { clone, isNumeric, toComparable, toNumber, toSafeString, valueIsEmpty } from '@/utils/tools'
import { computed, nextTick, onMounted, ref, Slots, toRef, useSlots, watch } from 'vue'
import PropertyLabelComponent from './PropertyLabelComponent.vue'
import TinyPropertyStateChangeComponent from './TinyPropertyStateChangeComponent.vue'
import TinyPropertyStateComponent from './TinyPropertyStateComponent.vue'
import { usePropertyReturns } from '@/composables/usePropertyReturns'
import { getMainName } from '@/utils/productUtils.js'

interface Props {
  property: EditableAdvancedProperty
  editMode?: boolean
  product?: SimplifiedProduct
  productInput?: ProductInput
  alwaysShowLabel?: boolean // whether to show label even when editable is not set
  tag?: string // html tag
  discreetInputs?: boolean // removes borders and padding for top level inputs and buttons (used by archivelistcomponent)
  showChanges?: boolean // if set, displays both original and current value
  activateFilterLinks?: boolean
  undoIcon?: 'fa-undo' | 'fa-times' | null // icon for undo button. null hides button
  showPropertyInfo?: boolean // shows property info on hover
  updateKey?: string | number // triggers update current
  showIfContains?: PropertyVisibility | 'changes'
  alwaysResettable?: boolean
  valueOnly?: boolean
  showValueAsHtml?: boolean
  allowNewValues?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  tag: 'div',
  undoIcon: 'fa-undo',
  showValueAsHtml: true,
})

const emit = defineEmits<{
  set: [data: { property: EditableAdvancedProperty; previousValue?: PropertySetterValueType; newValue: PropertySetterValueType; containsChanges: boolean }]
  reset: [data: { containsChanges: boolean }]
  input: [data: { value: PropertySetterValueType; containsChanges: boolean }]
  new: []
}>()

// --------------------------
// -- Composables & Stores --
// --------------------------
const store = useDefaultStore()
const propertyStore = usePropertyStore()
const tableUtilityStore = useProductsTableUtilityStore()
const { translate, appTranslate } = useTranslation()
const { original, current, updateCurrentPropertyReturn } = usePropertyReturns(toRef(props, 'property'), toRef(props, 'product'), toRef(props, 'productInput'))
const slots = useSlots() as Slots

const valueSeparator = props.property.type === 'categories' ? '&nbsp;»&nbsp;' : ',&nbsp;'

// -------------------
// -- Reactive vars --
// -------------------
const root = ref<HTMLElement>()

// const options = ref<HierarchicalOption<string | number | undefined>[]>([])
const selectedOptions = ref<PropertyOptions>()
const numericPlaceholder = ref('')
const initialized = ref(false) // used to ensure customdropdown doesn't reset modelValue before stagedOptions are set

const innerSearchTerm = ref<string | number>('') // for customdropdown, used by status placeholders
const categoryOptionsUpdateKey = ref(0)

// --------------------
// -- Computed props --
// --------------------
const htmlTag = computed(() => (props.editMode ? 'div' : props.tag))

const label = computed(() => PropertyService.getTranslatedHeader(props.property, store.translationLanguage))
const labelShouldBeShown = computed(() => !props.discreetInputs && label.value && (props.alwaysShowLabel || props.editMode))

const propShouldBeNumeric = computed(() => valueTypeIsNumeric(props.property?.valueType))
const restrictedToNumericValue = computed(() => {
  if (propShouldBeNumeric.value) {
    // check if existing value is also numeric. if not, it will be shown as text (input type number does not display text)
    // if (isNumeric(stagedOptions?.value?.[0]) || valueIsEmpty(stagedOptions.value?.[0])) {
    if (isNumeric(original.value?.defaultOptions?.[0]) || valueIsEmpty(original.value?.defaultOptions?.[0])) {
      return true
    }
    return false
  }
  return false
})

const propertyValue = computed({
  get(): PropertySetterValueType {
    if (shouldUseCustomDropdown.value) {
      return selectedOptions.value || []
    } else if (restrictedToNumericValue.value) {
      return isNumeric(selectedOptions.value?.[0]) ? toNumber(selectedOptions.value?.[0]) : selectedOptions.value === null ? null : ''
    }

    if (selectedOptions.value === null) {
      return null
    }

    return selectedOptions.value?.[0] ?? ''
  },
  set(value: SimplePropertySetterValueType) {
    const formattedValue = modelSetter(value)
    ensureCorrectNAValue(formattedValue) || setPropertyValue(formattedValue)
  },
})

const isStatic = computed(() => !props.editMode || !propertyIsWritable.value)
const hasValue = computed(() => !!selectedOptionsString.value?.length)

const matchesContainsRule = computed(() => {
  switch (props.showIfContains) {
    case 'any-data':
      return !!(
        current.value?.options?.length ||
        current.value?.status ||
        original.value?.options?.length ||
        original.value?.status ||
        current.value?.fallback !== undefined ||
        original.value?.fallback !== undefined
      )
    case 'value':
      return !!(
        current.value?.options?.length ||
        original.value?.options?.length ||
        current.value?.fallback !== undefined ||
        original.value?.fallback !== undefined
      )
    case 'value-or-preliminary':
      return !!(
        current.value?.options?.length ||
        current.value?.status === 'preliminary' ||
        original.value?.options?.length ||
        original.value?.status === 'preliminary' ||
        current.value?.fallback !== undefined ||
        original.value?.fallback !== undefined
      )
    case 'changes': {
      return containsChanges.value
    }
  }
  return true
})

const selectedOptionsString = computed(() => selectedOptions.value?.join('_'))

const hasModifiedValue = computed(() => (selectedOptionsString.value ?? '') !== (original.value?.defaultOptions?.join('_') ?? ''))

const hasChangedStatus = computed(() => !!current.value?.modified?.status)
const containsChanges = computed(() => hasChangedStatus.value || hasModifiedValue.value)

const hasRemovedValue = computed(() => {
  if (!original.value?.defaultOptions) {
    return false
  }

  const modelValueSet = new Set(selectedOptions.value)
  return Array.from(original.value?.defaultOptions).some((option) => !modelValueSet.has(option))
})

const hoverTitle = computed(() => {
  if (!labelShouldBeShown.value && !props.editMode) {
    if (props.showPropertyInfo) {
      return [
        label.value || toSafeString(current.value?.displayValue),
        props.property.info?.[store.infoLanguage],
        `${(appTranslate('Included in %s'), [belongsTo.value?.join(', ')])}`,
      ]
        .filter(Boolean)
        .join('\n\n')
    } else {
      return current.value?.displayValue?.toString()
    }
  }
  return undefined
})

const statePlaceholder = computed(() => current.value?.placeholder?.toString())

const shouldUseCustomDropdown = computed(
  () =>
    props.property.valueType !== 'date' &&
    (props.property.hasTerms || (props.property.allowMultipleValues && props.property.type !== 'attribute') || props.property.computedOptions?.length),
)

const propertyIsWritable = computed(() => props.productInput && productPropertyIsEditable(props.property, props.product, props.productInput))

const belongsTo = computed(() => propertyStore.getProfilesThatPropertyBelongsTo(props.property))

const isSku = computed(() => isSkuProperty(props.property))

const propertyIsResettable = computed(() => props.product && props.productInput && (!props.property.undeletable || !!original.value?.displayValue))

// --------------
// -- Watchers --
// --------------
watch(
  () => [original, props.editMode, props.updateKey],
  () => {
    updateCurrent()
  },
)

watch(
  () => props.editMode,
  async () => {
    innerSearchTerm.value = ''
    selectedOptions.value = current.value?.defaultOptions
  },
)

// ----------------------
// -- Life cycle hooks --
// ----------------------
onMounted(async () => {
  updateCurrent()

  initialized.value = true
})

// --------------------
// -- Event handlers --
// --------------------

const modelSetter = (value: SimplePropertySetterValueType) => {
  let formattedValue = value

  if (props.property.valueType === 'int' && restrictedToNumericValue.value && isNumeric(value)) {
    // remove decimals for int value types
    formattedValue = Math.round(value as number)
  } else if (isSku.value) {
    formattedValue = toSafeString(value).replace(/\s/g, '')
  } else if (isAvailableStatusProperty(props.property) && Array.isArray(value)) {
    formattedValue = value?.[0]
  }

  if (formattedValue !== undefined && formattedValue !== null && !Array.isArray(formattedValue)) {
    selectedOptions.value = [formattedValue]
  } else {
    selectedOptions.value = formattedValue
  }

  return formattedValue
}

const handleFocusedInput = (e: KeyboardEvent) => {
  if (props.property.valueType === 'date' && e.key !== 'Enter' && e.key !== 'Escape') {
    e.stopPropagation()
  }

  if (e.key === 'Escape') {
    // e.stopPropagation()
    ;(e.target as HTMLElement).blur()
  }

  if (propertyHasStaticValues(props.property)) {
    if (
      !['Enter', 'Tab', 'Escape', 'Shift'].includes(e.key) ||
      // ((e.shiftKey || e.altKey || e.ctrlKey) && e.key === 'KeyZ') || // allow undo
      (props.property.multiLine && e.key === 'Enter' && e.shiftKey)
    ) {
      // HTML Inputs stops global keydown listener when focused unless one of the listed keys are pressed
      e.stopPropagation()
    }

    if (propertyCanGenerateEan(props.property) && ['ArrowUp', 'ArrowDown'].includes(e.key)) {
      e.preventDefault() // disable numeric stepping for EAN-fields
    }
  } else {
    if (['ArrowUp', 'ArrowRight', 'ArrowDown', 'ArrowLeft'].includes(e.key)) {
      e.stopPropagation()
    }
  }
}

const handleResetStaticValue = () => {
  if (props.alwaysResettable && propertyIsResettable.value) {
    const productId = (props.product?.hasTemporarySku ? getMainName(props.product) : props.product?.sku) || props.productInput?.sku
    if (
      props.product &&
      confirm(appTranslate(`Do you want to reset "%s" for %s?`, [label.value, productId || appTranslate('new product', undefined, 'lowercase')]))
    ) {
      resetProperty()
      propertyStore.incPropertyResetKey(props.product.sku, props.property.key)
      emit('reset', { containsChanges: false })
    }
  }
}

const handleInput = (e: Event) => {
  const value = (e.target as HTMLInputElement | null)?.value

  if (value !== undefined && props.product) {
    // solution for v-model.lazy to immediately set modified without emitting change
    const formattedValue = modelSetter(value)
    emit('input', { value: formattedValue, containsChanges: containsChanges.value })
  }
}

// ---------------
// -- Functions --
// ---------------
/**
 * Focuses field / opens dropdowns unless it's the category property
 * @param focusedByKeyboard
 * @param shouldClear whether to clear the value before focus
 */
const focusField = (focusedByKeyboard?: boolean, shouldClear?: boolean) => {
  if (props.property.type !== 'categories') {
    if (shouldClear) {
      // setPropertyValue(null, false)
      selectedOptions.value = undefined
    }

    if (focusedByKeyboard) {
      setFocus()
    } else {
      // used by double click. seems to need the extra delay, but doesn't work with key strokes
      setTimeout(async () => {
        await nextTick()
        setFocus()
      })
    }
  }
}

const setFocus = () => {
  const element = root.value?.querySelector<HTMLInputElement>('input, *[contenteditable=true]')
  element?.focus()
}

const updateCurrent = () => {
  const previous = clone(current.value)

  updateCurrentPropertyReturn()
  selectedOptions.value = current.value?.defaultOptions

  const modifiedSinceLastUpdate =
    previous &&
    (previous.displayValue !== current.value?.displayValue || previous.delete !== current.value?.delete || previous.status !== current.value?.status)

  modifiedSinceLastUpdate && props.product && tableUtilityStore.clearCopiedIfAffected(props.product, props.property)
}

const setPropertyValue = (value: SimplePropertySetterValueType, doEmit = true) => {
  const previousValue = propertyValue.value //current.value?.defaultOptions
  props.property.set(props.productInput, value, props.product)

  if ((value || value === 0) && current.value?.status === 'checked') {
    // remove checked status if there is a value
    props.property.setStatus(props.productInput, null, props.product)
  }
  updateCurrent()

  if (doEmit) {
    emit('set', {
      property: props.property,
      previousValue,
      newValue: value,
      containsChanges: containsChanges.value,
    })
  }
}

const resetProperty = () => {
  if (!propertyIsWritable.value || !propertyIsResettable.value) {
    return
  }
  props.property.resetStatus(props.productInput)
  props.property.reset(props.productInput, props.product)
  updateCurrent()

  emit('reset', { containsChanges: containsChanges.value })
}

// enhances the number input validation. only numeric values are accepted, and comma as decimal separator if decimals are allowed
// certain characters allowed by the number input (.-+e) would otherwise cause the value to be erased when saved
const validateNumericInput = (e: Event, allowDecimals: boolean) => {
  const event = e as InputEvent
  const regexp = allowDecimals ? /[0-9,]/ : /[0-9]/
  if (event.data !== null && !regexp.test(event.data || '')) {
    e.preventDefault()
  }
}

/**
 * Converts status placeholders that were mistakenly added as a value to a status
 * @returns true if value was converted (property value and/or status was set)
 */
const ensureCorrectNAValue = (value: SimplePropertySetterValueType) => {
  const comparableValue = value?.toString().toUpperCase()

  if (value && (comparableValue === EmptyCheckedPlaceholder || comparableValue === EmptyPreliminaryPlaceholder)) {
    const status = comparableValue === EmptyCheckedPlaceholder ? 'checked' : 'preliminary'

    store.addMessage(
      new Message(appTranslate('Converting value "%s" to status %s', [value, status]), appTranslate('Tip: Set status via the context menu'), 'info', 7),
    )

    props.property.set(props.productInput, null, props.product)
    props.property.setStatus(props.productInput, status, props.product)
    updateCurrent()
    return true
  }
  return false
}

const options = computed(() => propertyStore.getPropertyOptions(props.property))

defineExpose({ focusField, current, property: props.property, product: props.product, containsChanges })
</script>

<style lang="scss" scoped>
.static {
  width: 100%;

  // &.has-value,
  // &.show-changes,
  // &.checked,
  // &.preliminary {
  &:not(.empty) {
    .label::after {
      content: ':';
      font-weight: bold;
      padding-left: 1px;
    }
  }

  .label {
    margin-right: 0.3rem;
  }
}

button.undo {
  position: absolute;
  top: -2px;
  right: -4px;
  transform: translateX(100%);
  background-color: white;
  border: 1px solid #aaa;
  font-size: 0.9em;
  border-radius: 0.25rem;
  padding: 0.1em 0.4em;
  z-index: 1; // 0;
}

input {
  width: 0;
  flex: 1;

  &[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}

.multi-line {
  cursor: auto;
  white-space: normal;
  overflow: auto;

  height: unset;
  overflow: auto;
}

.resettable {
  cursor: default;
  position: relative;
  display: inline-block;

  .resettable-icon {
    display: none;
  }

  &:hover {
    *:not(i) {
      background-color: #ddd;
      cursor: pointer;
    }

    .resettable-icon {
      position: absolute;
      display: block;
      transform: translateX(-150%);
    }
  }
}

.discreet {
  // .available-countries {
  //   flex-wrap: nowrap;

  //   :not(:last-child)::after {
  //     content: ', ';
  //     margin-left: 0;
  //     margin-right: 0.25em;
  //     color: var(--text-colour);
  //   }
  // }

  textarea {
    padding: 2px;
  }

  .form-control {
    height: unset;
    border: none !important;
    outline: none !important;
    border-radius: 0 !important;

    &:focus {
      box-shadow: unset !important;
    }
  }
}

.no-value .date {
  // position: relative;

  // &::after {
  //   content: '';
  //   position: absolute;
  //   left: 1em;
  //   width: 6em;
  //   top: 50%;
  //   height: 1px;
  //   background-color: var(--disabled);
  // }

  input[type='date'] {
    color: var(--disabled);
  }
}

// ----------------------
//  Inherited property related css
.property,
:deep() {
  :not(.placeholder) .state {
    // color: var(--text-colour);

    &:focus {
      border-color: #80bdff;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }
  }

  .no-value {
    &::after,
    .property-label,
    .fa-info-circle,
    .info-circle {
      opacity: 0.35;
    }
  }

  .form-control {
    font-size: inherit;
    color: inherit;
    background-color: inherit;
  }
}
</style>
